import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardBody } from 'reactstrap';

import AppSettingsService from 'services/appSettings';
import CancelModal from 'components/manageSessionModal/cancelModal';
import { toUkDisplayDate } from 'helpers/ukDate';
import { SECTION_AUDIENCE, SECTION_LIST, SETTABLE_AUDIENCES, fixedAudience } from 'constants/sections';

const LABELS = {
    [SECTION_AUDIENCE.ALL]: 'Doctors and Admins',
    [SECTION_AUDIENCE.ADMINS]: 'Admins and SuperAdmins',
    [SECTION_AUDIENCE.SUPER_ADMIN]: 'SuperAdmin only',
};

// A locked row shows the choices around the one it always has, all disabled: a super-admin-only row
// its two, the Admins-and-super-admins row (Clinicians management) all three.
const LOCKED_CHOICES = {
    [SECTION_AUDIENCE.SUPER_ADMIN]: [SECTION_AUDIENCE.ALL, SECTION_AUDIENCE.SUPER_ADMIN],
    [SECTION_AUDIENCE.ADMINS]: SETTABLE_AUDIENCES,
};

// What a locked row says, from the audience it always has.
const LOCKED_TEXT = {
    [SECTION_AUDIENCE.SUPER_ADMIN]: 'Always super admins only',
    [SECTION_AUDIENCE.ADMINS]: 'Always Admins and super admins',
};

// How far each audience opens a section. Choosing one that opens it further than the row shows asks
// first; one that opens it less far is the safe direction and saves straight away.
const REACH = {
    [SECTION_AUDIENCE.SUPER_ADMIN]: 0,
    [SECTION_AUDIENCE.ADMINS]: 1,
    [SECTION_AUDIENCE.ALL]: 2,
};

/**
 * The server's rows, one per known section in our order; anything missing - or an audience the row
 * does not offer - reads as superAdmin only. A fixed section is a locked row with the audience it
 * always has, whatever the row says: Statistics, Staff activity and Log history superAdmin, Clinicians
 * management 'admins' (Admins and super admins).
 */
function toRows(sections) {
    const byKey = {};
    (sections || []).forEach((row) => { byKey[row.section] = row; });

    return SECTION_LIST.map((section) => {
        const { key, label, audiences } = section;
        const row = byKey[key] || {};
        const fixed = fixedAudience(section);
        const locked = Boolean(fixed);
        const choices = locked ? LOCKED_CHOICES[fixed] : (audiences || LOCKED_CHOICES[SECTION_AUDIENCE.SUPER_ADMIN]);
        let audience = SECTION_AUDIENCE.SUPER_ADMIN;
        if (locked) {
            audience = fixed;
        } else if (choices.indexOf(row.audience) !== -1) {
            audience = row.audience;
        }

        return {
            key,
            label,
            locked,
            choices,
            audience,
            updatedAt: row.updatedAt || null,
            updatedBy: row.updatedBy || null,
        };
    });
}

function lastChanged(row) {
    if (row.locked) {
        return LOCKED_TEXT[row.audience];
    }
    if (!row.updatedAt) {
        return 'Not changed yet - super admins only';
    }

    return `Last changed${row.updatedBy ? ` by ${row.updatedBy.name}` : ''} on ${toUkDisplayDate(row.updatedAt)}`;
}

function openWarning(row, audience) {
    if (audience === SECTION_AUDIENCE.ADMINS) {
        return `Every Admin will see ${row.label} in their menu and can open it. Doctors will not.`;
    }

    return `Every Doctor and Admin will see ${row.label} in their menu and can open it.`;
}

/**
 * Who sees Activation codes and Vouchers. superAdmin only. Three choices: 'all' opens a section to
 * Doctors AND Admins alike, 'admins' to Admins but not Doctors, 'superAdmin' keeps it to super admins.
 * Hide only: a section someone may not see leaves their menu and its URL bounces them; its API is not
 * gated by this. Opening a section further (to Admins, or on to Doctors too) asks first; limiting it
 * again is the safe direction and saves straight away.
 *
 * Statistics, Staff activity and Log history are listed too, as locked rows: always super admins only,
 * their choices disabled, and no change is ever sent for them (the server would refuse one). Clinicians
 * management is a locked row as well, always Admins and super admins (a fixed rule, not a setting).
 */
export default function SectionAccessCard() {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    // { row, audience, expected }: the section "Open ... to Doctors and Admins?" (or "to Admins and
    // SuperAdmins?") is about. Kept after the dialog closes so its text does not change while it fades out.
    const [pendingOpen, setPendingOpen] = useState(null);
    const [isConfirming, setIsConfirming] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await AppSettingsService.getSectionAccess();
            setRows(toRows(data.sections));
            setError('');
        } catch (e) {
            setError('Could not load section access.');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (section, audience, expectedAudience) => {
        setIsSaving(true);
        try {
            const { data } = await AppSettingsService.setSectionAccess(section, audience, expectedAudience);
            setRows(toRows(data.sections));
            setError('');
        } catch (e) {
            // The API's message (e.g. someone else changed it meanwhile) has already been shown;
            // show what is actually in force now.
            await load();
        } finally {
            setIsSaving(false);
        }
    };

    const choose = (row, audience) => {
        if (row.locked || isSaving || audience === row.audience || row.choices.indexOf(audience) === -1) {
            return;
        }
        if (REACH[audience] > REACH[row.audience]) {
            setPendingOpen({ row, audience, expected: row.audience });
            setIsConfirming(true);
            return;
        }
        save(row.key, audience, row.audience);
    };

    const confirmOpen = () => {
        if (!isConfirming || isSaving) {
            return;
        }
        setIsConfirming(false);
        save(pendingOpen.row.key, pendingOpen.audience, pendingOpen.expected);
    };

    return (
        <Card className="card-auth overflow-hidden" data-testid="section-access-card">
            <CardBody>
                <label className="details-caption p-2">Section access</label>
                <div className="p-2">
                    <div className="text-muted font-size-12 mb-3">
                        <p className="mb-1">Choose who sees each section. Super admins always see every section.</p>
                        <ul className="pl-3 mb-0">
                            <li><strong>Doctors and Admins</strong>: every Doctor and every Admin.</li>
                            <li><strong>Admins and SuperAdmins</strong>: Admins see it; Doctors do not.</li>
                            <li><strong>SuperAdmin only</strong>: nobody else sees it.</li>
                        </ul>
                    </div>

                    {error ? <div className="text-danger mb-2" role="alert">{error}</div> : null}

                    {rows ? rows.map(row => (
                        <div key={row.key} className="mb-3" data-testid={`section-access-${row.key}`}>
                            <div className="font-weight-medium mb-1">{row.label}</div>
                            <div
                                className={`mh-options${row.locked ? ' mh-options--locked' : ''}`}
                                role="radiogroup"
                                aria-label={row.label}
                                aria-disabled={row.locked ? 'true' : undefined}
                            >
                                {row.choices.map(value => (
                                    <label
                                        key={value}
                                        className={`mh-option${row.audience === value ? ' mh-option--selected' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name={`sectionAccess-${row.key}`}
                                            value={value}
                                            checked={row.audience === value}
                                            disabled={isSaving || row.locked}
                                            onChange={() => choose(row, value)}
                                        />
                                        <span>{LABELS[value]}</span>
                                    </label>
                                ))}
                            </div>
                            <p className="text-muted font-size-12 mt-1 mb-0" data-testid={`section-access-${row.key}-changed`}>
                                {lastChanged(row)}
                            </p>
                        </div>
                    )) : (!error && <div className="text-muted">Loading…</div>)}
                </div>
            </CardBody>

            <CancelModal
                visibleModal={isConfirming}
                setVisibleModal={setIsConfirming}
                handleClose={confirmOpen}
                header={pendingOpen ? `Open ${pendingOpen.row.label} to ${LABELS[pendingOpen.audience]}?` : ''}
                text={pendingOpen ? openWarning(pendingOpen.row, pendingOpen.audience) : ''}
                cancelButtonTitle="Cancel"
            />
        </Card>
    );
}
